import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Btn, Chip, DataTable, Loading, Msg, Section, Select, TextInput, fmtDate, useAdminGet } from '../kit';
import { Pager, Pill, num, pageInfo, rowsOf, str, useDebounced } from '../ipintel/shared';
import { ExcelButton } from '../techpages/shared';
import {
  CONFIDENCES, ConfidencePill, EXPORT_PATH, HORIZONS, type HzCtx, HorizonPill, LEVERS, LeverChips, type Preset,
  RESULT_EMPTY, type ResultFilters, S, applyPreset, clip, fmtCost, horizonLabel, leversOf, promptLabel, shortId,
  uniOptions,
} from './shared';

/**
 * Results — GET horizon/results: every Horizon sandbox result (all prompts,
 * all runs; ~80k rows), paged, filtered and sorted on the server. A row opens
 * the result drawer (every column + the raw model response); the technology
 * opens its own drawer. The Excel export takes the same filters (capped at
 * 100,000 rows server-side).
 */

const SORT_KEYS: Record<string, string> = {
  created_at: 'created', tech_title: 'title', university_name: 'university', prompt_id: 'prompt', run_id: 'run',
  horizon: 'horizon', horizon_confidence: 'confidence', tokens: 'tokens', cost: 'cost', latency_ms: 'latency',
};
const COLUMNS = ['created_at', 'tech_title', 'university_name', 'prompt_id', 'run_id', 'horizon', 'horizon_confidence',
  'industry', 'value_lever_tags', 'primary_problem_type', 'tokens', 'cost', 'latency_ms', 'error', 'reviewed', 'approved'];
const LABELS: Record<string, string> = {
  created_at: 'Created', tech_title: 'Technology', university_name: 'University', prompt_id: 'Prompt', run_id: 'Run',
  horizon: 'Horizon', horizon_confidence: 'Confidence', industry: 'Industry', value_lever_tags: 'Value levers',
  primary_problem_type: 'Problem type', tokens: 'Tokens', cost: 'Cost', latency_ms: 'Latency', error: 'Error',
  reviewed: 'Reviewed', approved: 'Approved',
};
const YESNO = (what: string, yes: string, no: string) => ({
  options: ['', '1', '0'], labels: { '': what, '1': yes, '0': no } as Record<string, string>,
});
const ERR = YESNO('Errors or not', 'Only errors', 'No errors');
const REV = YESNO('Reviewed or not', 'Reviewed', 'Not reviewed');
const APP = YESNO('Approved or not', 'Approved', 'Not approved');
const bool = (v: any) => v === true || v === 1 || v === '1' || v === 'true' || v === 't';

const HzResults: React.FC<{ ctx: HzCtx; preset: Preset<ResultFilters> }> = ({ ctx, preset }) => {
  const last = useRef(preset?.n || 0);
  const [f, setF] = useState<ResultFilters>(() => ({ ...RESULT_EMPTY, ...(preset?.f || {}) }));
  useEffect(() => { applyPreset(preset, RESULT_EMPTY, setF, last); }, [preset]);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>({ key: 'created_at', dir: 'desc' });
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [tick, setTick] = useState(0);
  const [dlErr, setDlErr] = useState('');
  const set = (k: keyof ResultFilters) => (v: string) => setF(s => ({ ...s, [k]: v }));
  const q = useDebounced(f.q);
  const industry = useDebounced(f.industry);
  const tag = useDebounced(f.tag);

  const filterParams = useMemo(() => {
    const p: Record<string, string | number> = {
      q, prompt: f.prompt, run: f.run, university: f.university, horizon: f.horizon, confidence: f.confidence,
      industry, lever: f.lever, tag, error: f.error, reviewed: f.reviewed, approved: f.approved, from: f.from, to: f.to,
    };
    if (sort?.key && SORT_KEYS[sort.key]) { p.sort = SORT_KEYS[sort.key]; p.dir = sort.dir; }
    return p;
  }, [q, industry, tag, f.prompt, f.run, f.university, f.horizon, f.confidence, f.lever, f.error, f.reviewed,
    f.approved, f.from, f.to, sort]);
  const filterKey = JSON.stringify(filterParams);
  useEffect(() => { setPage(0); }, [filterKey, perPage]);

  const res = useAdminGet(S, 'results', { ...filterParams, page, per_page: perPage },
    [filterKey, page, perPage, tick, ctx.dataTick]);
  const d = res.data || {};
  const rows = rowsOf(d, 'results');
  const per = Number(perPage) || 50;
  const { total, pages } = pageInfo(d, rows, per);

  const uni = uniOptions(ctx.universities);
  const promptOpts = ['', ...ctx.prompts.map(p => p.id)];
  const promptLabels: Record<string, string> = {
    '': 'Any prompt', ...Object.fromEntries(ctx.prompts.map(p => [p.id, clip(`${p.is_final ? '★ ' : ''}${promptLabel(p)}`, 40)])),
  };
  const runLabel = (r: any) => {
    const when = r?.created_at ? new Date(r.created_at) : null;
    const w = when && !isNaN(when.getTime()) ? when.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '';
    return clip(`${shortId(r?.id)} · ${w}${r?.results !== undefined ? ` · ${num(r.results).toLocaleString()}` : ''} · ${str(r?.prompt_name)}`, 44);
  };
  const runOpts = ['', ...ctx.runs.map((r: any) => str(r?.id)).filter(Boolean)];
  const runLabels: Record<string, string> = { '': 'Any run', ...Object.fromEntries(ctx.runs.map((r: any) => [str(r?.id), runLabel(r)])) };
  if (f.run && !runLabels[f.run]) { runOpts.push(f.run); runLabels[f.run] = shortId(f.run); }

  const chips: { label: string; value: string; clear: () => void }[] = [];
  if (f.q) chips.push({ label: 'Search', value: f.q, clear: () => set('q')('') });
  if (f.prompt) chips.push({ label: 'Prompt', value: promptLabel(ctx.promptById(f.prompt), f.prompt), clear: () => set('prompt')('') });
  if (f.run) chips.push({ label: 'Run', value: shortId(f.run), clear: () => set('run')('') });
  if (f.university) chips.push({ label: 'University', value: uni.labels[f.university] || f.university, clear: () => set('university')('') });
  if (f.horizon) chips.push({ label: 'Horizon', value: horizonLabel(f.horizon), clear: () => set('horizon')('') });
  if (f.confidence) chips.push({ label: 'Confidence', value: f.confidence, clear: () => set('confidence')('') });
  if (f.industry) chips.push({ label: 'Industry', value: f.industry, clear: () => set('industry')('') });
  if (f.lever) chips.push({ label: 'Lever', value: f.lever, clear: () => set('lever')('') });
  if (f.tag) chips.push({ label: 'Tag', value: f.tag, clear: () => set('tag')('') });
  if (f.error) chips.push({ label: 'Error', value: ERR.labels[f.error], clear: () => set('error')('') });
  if (f.reviewed) chips.push({ label: 'Reviewed', value: REV.labels[f.reviewed], clear: () => set('reviewed')('') });
  if (f.approved) chips.push({ label: 'Approved', value: APP.labels[f.approved], clear: () => set('approved')('') });
  if (f.from) chips.push({ label: 'From', value: f.from, clear: () => set('from')('') });
  if (f.to) chips.push({ label: 'To', value: f.to, clear: () => set('to')('') });

  return (
    <Section title="Results"
      right={
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-grayscale-1100">{total.toLocaleString()} result{total === 1 ? '' : 's'}</span>
          {chips.length > 0 && <Btn small onClick={() => setF(RESULT_EMPTY)}>Clear {chips.length} filter{chips.length === 1 ? '' : 's'}</Btn>}
          <Btn small onClick={() => setTick(t => t + 1)} disabled={res.loading}>Refresh</Btn>
          <ExcelButton path={EXPORT_PATH} params={{ kind: 'results', ...filterParams }} onError={setDlErr}
            fallback="horizon-results" what="These results (up to 100,000 rows)" />
        </div>
      }>
      <p className="text-xs text-grayscale-1100 -mt-1">
        Every result any Horizon prompt produced, in every sandbox and batch run. A technology can have several (one per
        prompt / run); its current one is chosen by the source priority (Overview). Click a row for all columns and the
        raw model response.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <TextInput value={f.q} onChange={set('q')} wide placeholder="Search title, tech id or university…" />
        <Select value={f.prompt} onChange={set('prompt')} options={promptOpts} labels={promptLabels} />
        <Select value={f.run} onChange={set('run')} options={runOpts} labels={runLabels} />
        <Select value={f.university} onChange={set('university')} options={uni.options} labels={uni.labels} />
        <Select value={f.horizon} onChange={set('horizon')} options={['', ...HORIZONS]}
          labels={{ '': 'Any horizon', ...Object.fromEntries(HORIZONS.map(h => [h, horizonLabel(h)])) }} />
        <Select value={f.confidence} onChange={set('confidence')} options={['', ...CONFIDENCES]} labels={{ '': 'Any confidence' }} />
        <input list="hz-result-industries" value={f.industry} onChange={e => set('industry')(e.target.value)}
          placeholder="Industry…" title="The result's industry (pick one or type)"
          className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-56 max-w-full" />
        <datalist id="hz-result-industries">{ctx.industries.map(i => <option key={i} value={i} />)}</datalist>
        <Select value={f.lever} onChange={set('lever')} options={['', ...LEVERS]} labels={{ '': 'Any value lever' }} />
        <TextInput value={f.tag} onChange={set('tag')} placeholder="Technical tag…" />
        <Select value={f.error} onChange={set('error')} options={ERR.options} labels={ERR.labels} />
        <Select value={f.reviewed} onChange={set('reviewed')} options={REV.options} labels={REV.labels} />
        <Select value={f.approved} onChange={set('approved')} options={APP.options} labels={APP.labels} />
        <span className="inline-flex items-center gap-1.5 text-xs text-grayscale-1100 flex-wrap">
          created from <TextInput type="date" value={f.from} onChange={set('from')} />
          to <TextInput type="date" value={f.to} onChange={set('to')} />
        </span>
      </div>
      {chips.length > 0 && (
        <div className="flex gap-1.5 flex-wrap">
          {chips.map(c => <Chip key={c.label} label={c.label} value={c.value} onClear={c.clear} />)}
        </div>
      )}
      {dlErr && <Msg kind="error">Export failed: {dlErr}</Msg>}
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {res.loading && !res.data ? <Loading label="Reading results…" /> : (
        <DataTable rows={rows} columns={COLUMNS} labels={LABELS} maxHeight="44rem"
          unsortable={COLUMNS.filter(c => !SORT_KEYS[c])}
          empty={chips.length ? 'No results match these filters.' : 'No results.'}
          serverSort={sort} onSortChange={(key, dir) => setSort(key && dir ? { key, dir } : null)}
          render={{
            created_at: r => <span className="text-xs">{fmtDate(r.created_at)}</span>,
            tech_title: r => (
              <span className="flex flex-col leading-tight max-w-[20rem]">
                <button type="button" onClick={() => ctx.openResult(str(r.id))} title="Open this result"
                  className="text-left font-medium text-grayscale-1300 underline underline-offset-2 truncate">
                  {str(r.tech_title) || str(r.utech_technology_id) || shortId(r.id)}
                </button>
                <span className="flex items-center gap-1.5 min-w-0">
                  <button type="button" onClick={() => ctx.openTech(str(r.utech_technology_id))}
                    title="Open this technology (current data + every result)"
                    className="text-left text-[10px] font-mono text-grayscale-1100 hover:underline truncate">
                    {str(r.utech_technology_id)}
                  </button>
                  {r.is_current === true && <Pill tone="good" title="The result its public page shows">current</Pill>}
                </span>
              </span>
            ),
            university_name: r => <span className="block max-w-[12rem] truncate" title={str(r.university_name)}>{str(r.university_name) || '—'}</span>,
            prompt_id: r => {
              const p = ctx.promptById(r.prompt_id);
              return (
                <button type="button" onClick={() => ctx.goPrompt(str(r.prompt_id))}
                  title={p ? promptLabel(p, r.prompt_id) : (str(r.prompt_name) || str(r.prompt_id))}
                  className="text-left text-xs hover:underline inline-flex items-center gap-1">
                  <span className="font-mono">{shortId(r.prompt_id)}</span>
                  {p?.is_final && <Pill tone="good">final</Pill>}
                </button>
              );
            },
            run_id: r => (
              <button type="button" onClick={() => set('run')(str(r.run_id))} title="Only this run"
                className="font-mono text-xs hover:underline">{shortId(r.run_id) || '—'}</button>
            ),
            horizon: r => <HorizonPill h={r.horizon} />,
            horizon_confidence: r => <ConfidencePill c={r.horizon_confidence} />,
            industry: r => <span className="block max-w-[12rem] truncate" title={str(r.industry)}>{str(r.industry) || '—'}</span>,
            value_lever_tags: r => <LeverChips levers={leversOf(r.value_lever_tags ?? r.levers)} max={2} compact />,
            primary_problem_type: r => <span className="block max-w-[12rem] truncate" title={str(r.primary_problem_type)}>{str(r.primary_problem_type) || '—'}</span>,
            tokens: r => <span className="tabular-nums">{num(r.tokens).toLocaleString()}</span>,
            cost: r => <span className="tabular-nums">{fmtCost(r.cost, true)}</span>,
            latency_ms: r => <span className="tabular-nums">{r.latency_ms ? `${(num(r.latency_ms) / 1000).toFixed(1)}s` : '—'}</span>,
            error: r => (str(r.error) ? <span title={str(r.error)}><Pill tone="bad">error</Pill></span> : <span className="text-grayscale-1100">—</span>),
            reviewed: r => (bool(r.reviewed) ? '✓' : '—'),
            approved: r => (r.approved === null || r.approved === undefined ? '—' : bool(r.approved) ? '✓' : '✗'),
          }}
          actions={r => <Btn small onClick={() => ctx.openResult(str(r.id))}>Open</Btn>} />
      )}
      {pages > 1 && (
        <Pager page={page} pages={pages} total={total} shown={rows.length} per={per}
          onPage={setPage} perPage={perPage} onPerPage={setPerPage} />
      )}
    </Section>
  );
};

export default HzResults;
